import { useState, useCallback, useMemo } from "react";
import { faFlask } from "@fortawesome/free-solid-svg-icons";
import type { DevToolsPlugin, PluginStatus } from "../../types";
import { FilterableList } from "../../FilterableList";
import type { FilterableSection } from "../../FilterableList";

interface Variant {
  key: string;
  value: string;
  isControl: boolean;
  isActive: boolean;
}

interface Experiment {
  name: string;
  label: string;
  variants: Variant[];
  hasActiveVariant: boolean;
}

interface AmplitudePluginProps {
  domainUserId?: string;
  experiments: string[];
  currentSite: string;
  amplitudeExperimentsDetailed?: {
    currentPage: Experiment[];
    allOthers: Experiment[];
  };
}

function csrfToken(): string {
  return (
    document
      .querySelector('meta[name="csrf-token"]')
      ?.getAttribute("content") || ""
  );
}

function AmplitudePanel({
  domainUserId,
  amplitudeExperimentsDetailed,
}: AmplitudePluginProps) {
  const [copied, setCopied] = useState(false);
  const [selectedExperiment, setSelectedExperiment] = useState<Experiment | null>(null);
  const [selectedVariantKey, setSelectedVariantKey] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const currentPage = amplitudeExperimentsDetailed?.currentPage || [];
  const allOthers = amplitudeExperimentsDetailed?.allOthers || [];
  const allExperiments = [...currentPage, ...allOthers];

  const copyDuid = useCallback(() => {
    if (!domainUserId) return;
    navigator.clipboard.writeText(domainUserId).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [domainUserId]);

  const selectExperiment = useCallback((exp: Experiment) => {
    setSelectedExperiment(exp);
    const active = exp.variants.find((v) => v.isActive);
    setSelectedVariantKey(active ? active.key : "__default__");
  }, []);

  const submitVariant = useCallback(() => {
    if (!selectedExperiment || !selectedVariantKey || !domainUserId) return;
    setSubmitting(true);

    const form = document.createElement("form");
    form.method = "POST";
    form.action = "/dev_tools/amplitude/add_user_to_variant";
    form.style.display = "none";

    const fields: Record<string, string> = {
      authenticity_token: csrfToken(),
      experiment_key: selectedExperiment.label,
      variant_key: selectedVariantKey,
      "user_ids[]": domainUserId,
      return_to: window.location.href,
    };

    Object.entries(fields).forEach(([name, value]) => {
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = name;
      input.value = value;
      form.appendChild(input);
    });

    document.body.appendChild(form);
    form.submit();
  }, [selectedExperiment, selectedVariantKey, domainUserId]);

  const clearOverrides = useCallback(() => {
    const form = document.createElement("form");
    form.method = "POST";
    form.action = "/dev_tools/amplitude/clear_overrides";
    form.style.display = "none";

    const tokenInput = document.createElement("input");
    tokenInput.type = "hidden";
    tokenInput.name = "authenticity_token";
    tokenInput.value = csrfToken();
    form.appendChild(tokenInput);

    const returnInput = document.createElement("input");
    returnInput.type = "hidden";
    returnInput.name = "return_to";
    returnInput.value = window.location.href;
    form.appendChild(returnInput);

    document.body.appendChild(form);
    form.submit();
  }, []);

  const hasOverrides = allExperiments.some((e) => e.hasActiveVariant);

  const renderExperiments = useCallback(
    (experiments: Experiment[]) => (
      <>
        {experiments.map((exp) => (
          <ExperimentRow
            key={exp.label}
            experiment={exp}
            isSelected={selectedExperiment?.label === exp.label}
            onClick={() => selectExperiment(exp)}
          />
        ))}
      </>
    ),
    [selectedExperiment, selectExperiment],
  );

  const sections: FilterableSection<Experiment>[] = useMemo(
    () => [
      ...(currentPage.length > 0
        ? [{
            key: "currentPage",
            title: "Current Page",
            items: currentPage,
            matchText: (e: Experiment) => `${e.name} ${e.label}`,
            renderItems: renderExperiments,
            defaultOpen: true,
          }]
        : []),
      ...(allOthers.length > 0
        ? [{
            key: "allOthers",
            title: currentPage.length > 0 ? "All Other" : "Experiments",
            items: allOthers,
            matchText: (e: Experiment) => `${e.name} ${e.label}`,
            renderItems: renderExperiments,
          }]
        : []),
    ],
    [currentPage, allOthers, renderExperiments],
  );

  if (!domainUserId) {
    return (
      <div className="dt-status-banner dt-status-banner--warning">
        <div className="dt-status-banner__title">No DUID detected</div>
        <div className="dt-status-banner__text">
          Snowplow domain user ID not found in cookies
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="dt-section-label">Domain User ID</div>
      <div className="dt-amplitude__duid-row">
        <span className="dt-amplitude__duid-value">{domainUserId}</span>
        <button className="dt-btn" onClick={copyDuid} type="button">
          {copied ? "Copied!" : "Copy"}
        </button>
      </div>

      {allExperiments.length > 0 ? (
        <FilterableList sections={sections} placeholder="Filter experiments..." />
      ) : (
        <div className="dt-status-banner">
          <div className="dt-status-banner__text">
            No experiments available for this region
          </div>
        </div>
      )}

      {selectedExperiment && (
        <div className="dt-amplitude__variants">
          <div className="dt-section-label">
            Variant for {selectedExperiment.name}
          </div>

          <label
            className={`dt-amplitude__variant ${
              selectedVariantKey === "__default__"
                ? "dt-amplitude__variant--selected"
                : ""
            } ${
              !selectedExperiment.hasActiveVariant
                ? "dt-amplitude__variant--active"
                : ""
            }`}
          >
            <input
              type="radio"
              className="dt-amplitude__variant-radio"
              name="variant"
              value="__default__"
              checked={selectedVariantKey === "__default__"}
              onChange={() => setSelectedVariantKey("__default__")}
            />
            <div className="dt-amplitude__variant-info">
              <div className="dt-amplitude__variant-key">No Override</div>
              <div className="dt-amplitude__variant-value">
                Use Amplitude assignment
              </div>
            </div>
            {!selectedExperiment.hasActiveVariant && (
              <span className="dt-amplitude__variant-badge dt-amplitude__variant-badge--active">
                Active
              </span>
            )}
          </label>

          {selectedExperiment.variants.map((variant) => (
            <label
              key={variant.key}
              className={`dt-amplitude__variant ${
                selectedVariantKey === variant.key
                  ? "dt-amplitude__variant--selected"
                  : ""
              } ${variant.isActive ? "dt-amplitude__variant--active" : ""}`}
            >
              <input
                type="radio"
                className="dt-amplitude__variant-radio"
                name="variant"
                value={variant.key}
                checked={selectedVariantKey === variant.key}
                onChange={() => setSelectedVariantKey(variant.key)}
              />
              <div className="dt-amplitude__variant-info">
                <div className="dt-amplitude__variant-key">{variant.key}</div>
                <div className="dt-amplitude__variant-value">
                  {variant.value}
                </div>
              </div>
              <span
                className={`dt-amplitude__variant-badge ${
                  variant.isControl
                    ? "dt-amplitude__variant-badge--control"
                    : "dt-amplitude__variant-badge--treatment"
                }`}
              >
                {variant.isControl ? "Control" : "Treatment"}
              </span>
              {variant.isActive && (
                <span className="dt-amplitude__variant-badge dt-amplitude__variant-badge--active">
                  Active
                </span>
              )}
            </label>
          ))}

          <div className="dt-amplitude__actions">
            <button
              className="dt-btn dt-btn--primary"
              onClick={submitVariant}
              disabled={submitting}
              type="button"
            >
              {submitting ? "Applying..." : "Apply Variant"}
            </button>
          </div>
        </div>
      )}

      {hasOverrides && (
        <>
          <div className="dt-section-label" style={{ marginTop: 16 }} />
          <button
            className="dt-btn"
            onClick={clearOverrides}
            type="button"
            style={{ width: "100%" }}
          >
            Clear All Overrides
          </button>
        </>
      )}
    </>
  );
}


function ExperimentRow({
  experiment,
  isSelected,
  onClick,
}: {
  experiment: Experiment;
  isSelected: boolean;
  onClick: () => void;
}) {
  const className = [
    "dt-amplitude__experiment",
    isSelected ? "dt-amplitude__experiment--selected" : "",
    experiment.hasActiveVariant
      ? "dt-amplitude__experiment--has-override"
      : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <button type="button" className={className} onClick={onClick}>
      <div className="dt-amplitude__experiment-name">
        {experiment.hasActiveVariant && "✓ "}
        {experiment.name}
      </div>
      <div className="dt-amplitude__experiment-label">{experiment.label}</div>
    </button>
  );
}

export function amplitudePlugin(props: AmplitudePluginProps): DevToolsPlugin {
  return {
    id: "amplitude",
    name: "Amplitude",
    icon: faFlask,
    render: () => <AmplitudePanel {...props} />,
    useStatus: (): PluginStatus => {
      if (!props.domainUserId) {
        return { state: "inactive" };
      }
      const allExperiments = [
        ...(props.amplitudeExperimentsDetailed?.currentPage || []),
        ...(props.amplitudeExperimentsDetailed?.allOthers || []),
      ];
      const hasOverrides = allExperiments.some((e) => e.hasActiveVariant);
      if (hasOverrides) {
        const overrideCount = allExperiments.filter((e) => e.hasActiveVariant).length;
        return {
          state: "warning",
          badge: overrideCount,
          summary: `${overrideCount} variant override${overrideCount === 1 ? "" : "s"} active`,
        };
      }
      const currentPage =
        props.amplitudeExperimentsDetailed?.currentPage || [];
      if (currentPage.length > 0) {
        return {
          state: "active",
          badge: currentPage.length,
          summary: `${currentPage.length} experiment${currentPage.length === 1 ? "" : "s"} on this page`,
        };
      }
      return { state: "inactive" };
    },
  };
}
